import {
  getTg,
  preemptTelegramBottomBarForTabbarShow,
  syncTelegramChromeColors
} from '../core/telegram.js';
import { getTelegramInitData, apiFetchJSON } from '../core/api.js';
import { showView, setActiveTab } from './navigation.js';
import { initOwnerChatPingStat } from '../features/owner-chat/ping-stat.js';
import { initCartUi } from '../features/cart/cart.js';
import { initCheckoutUi } from '../features/checkout/ui.js';
import { initTemplateConfiguratorUi } from '../features/presets/config-wizard.js';
import { initLazyImages } from '../core/lazy-image.js';
import { updateMainPresetsPrice } from '../features/presets/list.js';
import { updateProfileView, preloadProfileAvatar } from '../features/profile/view.js';
import {
  getIsOwnerApp,
  setIsOwnerApp,
  setBuildBetaAccess,
  getOwnerTabBound,
  setOwnerTabBound,
  getTgThemeBound,
  setTgThemeBound,
  setCurrentUser
} from './app-state.js';
import type { MeApiResponse } from '../types/api.js';
import { applyBetaWipUi } from './beta-wip.js';

function bindTelegramChrome(tg: TelegramWebApp): void {
  try {
    tg.expand?.();
    tg.MainButton.hide();
    tg.ready?.();
    syncTelegramChromeColors();
    if (!getTgThemeBound() && typeof tg.onEvent === 'function') {
      try {
        tg.onEvent('themeChanged', syncTelegramChromeColors);
      } catch {
        /* ignore */
      }
      setTgThemeBound(true);
    }
  } catch {
    /* ignore */
  }
}

function applyOwnerUi(): void {
  const tabOwnerEl = document.getElementById('tab-owner');
  if (tabOwnerEl) tabOwnerEl.style.display = getIsOwnerApp() ? 'flex' : 'none';
  const aiCard = document.querySelector('.service-card[data-action="ai_assistant"]') as HTMLElement | null;
  if (aiCard) aiCard.style.display = getIsOwnerApp() ? '' : 'none';
  if (getIsOwnerApp() && tabOwnerEl && !getOwnerTabBound()) {
    setOwnerTabBound(true);
    tabOwnerEl.addEventListener('click', () => {
      preemptTelegramBottomBarForTabbarShow();
      setActiveTab(tabOwnerEl);
      showView('view-owner-presets', 'right');
    });
  }
  try {
    initOwnerChatPingStat();
  } catch {
    /* ignore */
  }
  try {
    applyBetaWipUi();
  } catch {
    /* ignore */
  }
}

const PRESENCE_HEARTBEAT_MS = 30_000;
let presenceTimer: ReturnType<typeof setInterval> | null = null;
let presenceLifecycleBound = false;

function sendPresenceHeartbeat(): void {
  if (document.hidden || !getTelegramInitData()) return;
  void apiFetchJSON('/api/presence', { method: 'POST' }).catch(() => {
    /* ignore */
  });
}

function sendPresenceLeave(): void {
  if (!getTelegramInitData()) return;
  void apiFetchJSON('/api/presence/leave', { method: 'POST', keepalive: true }).catch(() => {
    /* ignore */
  });
}

function onPresenceVisibilityChange(): void {
  if (document.hidden) sendPresenceLeave();
  else sendPresenceHeartbeat();
}

function bindPresenceLifecycle(): void {
  if (presenceLifecycleBound) return;
  presenceLifecycleBound = true;
  document.addEventListener('visibilitychange', onPresenceVisibilityChange);
  window.addEventListener('pagehide', sendPresenceLeave);
  const tg = getTg();
  if (tg && typeof tg.onEvent === 'function') {
    try {
      tg.onEvent('visibility_changed', (...args: unknown[]) => {
        const payload = args[0] as { is_visible?: boolean } | undefined;
        if (payload?.is_visible === false) sendPresenceLeave();
        else if (payload?.is_visible === true) sendPresenceHeartbeat();
      });
    } catch {
      /* older Telegram clients */
    }
  }
}

function startPresenceHeartbeat(): void {
  if (presenceTimer) return;
  bindPresenceLifecycle();
  sendPresenceHeartbeat();
  presenceTimer = setInterval(sendPresenceHeartbeat, PRESENCE_HEARTBEAT_MS);
}

async function initAppSession(): Promise<void> {
  if (!getTelegramInitData()) {
    applyOwnerUi();
    return;
  }
  try {
    const data = await apiFetchJSON<MeApiResponse>('/api/me', { method: 'GET' });
    if (data?.ok) {
      setIsOwnerApp(!!data.isOwner);
      setBuildBetaAccess(!!data.buildAccess);
      startPresenceHeartbeat();
    }
  } catch {
    /* ignore */
  }
  applyOwnerUi();
}

let tg = getTg();
if (tg) {
  bindTelegramChrome(tg);
}

(function waitForTelegram(): void {
  if (getTg()) {
    tg = getTg();
    try {
      setCurrentUser(tg?.initDataUnsafe?.user ?? null);
    } catch {
      /* ignore */
    }
    return;
  }
  let attempts = 0;
  const id = setInterval(() => {
    tg = getTg();
    if (tg) {
      clearInterval(id);
      setCurrentUser(tg.initDataUnsafe?.user ?? null);
      bindTelegramChrome(tg);
      try {
        updateProfileView();
      } catch {
        /* ignore */
      }
      try {
        preloadProfileAvatar();
      } catch {
        /* ignore */
      }
      try {
        void initAppSession();
      } catch {
        /* ignore */
      }
      return;
    }
    attempts++;
    if (attempts >= 50) clearInterval(id);
  }, 100);
})();

setTimeout(function preloadProfile(): void {
  try {
    void initAppSession()
      .then(() => {
        try {
          updateProfileView();
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        try {
          updateProfileView();
        } catch {
          /* ignore */
        }
      });
    try {
      initCartUi();
    } catch {
      /* ignore */
    }
    try {
      initCheckoutUi();
    } catch {
      /* ignore */
    }
    try {
      initTemplateConfiguratorUi();
    } catch {
      /* ignore */
    }
    try {
      initLazyImages();
    } catch {
      /* ignore */
    }
    updateMainPresetsPrice();
    const runAvatar = (): void => {
      try {
        preloadProfileAvatar();
      } catch {
        /* ignore */
      }
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(runAvatar, { timeout: 3000 });
    } else {
      setTimeout(runAvatar, 800);
    }
  } catch {
    /* ignore */
  }
}, 0);
